import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { urlParamReplicaId, urlProjectDocumentHistory } from '../../../Constants';
import { IAction, SlicePropsWithData } from '../../store';
import { IDocDefaultFields } from '../../project/documents/sliceProjectDocuments';
import { apiFetch } from '../../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IProjectDocumentHistoryState = {
    data: [],
    loading: false,
    error: null,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Async function that fetches version history data on the selected documents in a project.
     * @param config - Object that contains the project database's replicaId, target document number, and the document fields to return.
     */
    getProjectDocumentHistory: async (config: {
        replicaId: string;
        universalId: string;
    }): Promise<IDocumentVersionHistoryEntry[]> => {
        try {
            const { replicaId, universalId } = config;

            const request: RequestInit = {
                headers: {
                    fieldset: [
                        'docIndex',
                        'docNumber',
                        'creationDate',
                        'creator',
                        'subject',
                        'docStatus',
                        'wfTask',
                    ].join(';'),
                    universalId,
                },
            };

            const url = urlProjectDocumentHistory.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectDocumentHistoryState>) => {
        /**
         * Reducers for the 'getProjectDocumentHistory' thunk.
         */

        builder.addCase(
            getProjectDocumentHistory.pending,
            (state: Draft<IProjectDocumentHistoryState>) => {
                state.loading = true;
                state.error = null;
            },
        );
        builder.addCase(
            getProjectDocumentHistory.fulfilled,
            (
                state: Draft<IProjectDocumentHistoryState>,
                action: IAction<IDocumentVersionHistoryEntry[]>,
            ) => {
                state.loading = false;
                state.error = null;
                state.data = action.payload;
            },
        );
        builder.addCase(
            getProjectDocumentHistory.rejected,
            (state: Draft<IProjectDocumentHistoryState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
                state.data = [];
            },
        );
    },
};

/**
 * Expose thunk functions as exports.
 */
export const getProjectDocumentHistory = createAsyncThunk(
    'project/documentHistory',
    thunks.getProjectDocumentHistory,
);

/** Dictionary that contains the additional document children of project documents, with the document's NoteId being the object key.  */
export type DocumentCollection = { [id: string]: IDocDefaultFields[] };

/**
 * Interface that describes the state object for this redux slice.
 */
export type IProjectDocumentHistoryState = SlicePropsWithData<IDocumentVersionHistoryEntry[]>;

/**
 * Interface that describes the Object that is returned from a document version history request.
 */
export interface IDocumentVersionHistoryEntry extends IDocDefaultFields {
    /** Document version index. */
    docIndex: string;

    /** Document number. */
    docNumber: string;

    /** Document creation date. */
    creationDate: number;

    /** User that created the document. */
    creator: string;

    /** Document's subject text. */
    subject: string;

    /** Status of the document. */
    docStatus: string;

    /** Workflow task where the document is currently located. */
    wfTask: string;
}

/**
 * Create the redux slice.
 */
export const projectDocumentHistorySlice = createSlice({
    name: 'projectDocumentHistory',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export slice reducer as default.
 */
export default projectDocumentHistorySlice.reducer;
